import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class ProductRecommendations extends Component {
  #activeFetch;
  #cachedRecommendations = new Map();
  #intersectionObserver = new IntersectionObserver((entries, observer) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    this.#loadRecommendations();
  }, { rootMargin: '0px 0px 400px 0px' });

  #mutationObserver = new MutationObserver(() => {
    this.dataset.recommendationsPerformed = 'false';
    this.#loadRecommendations();
  });

  connectedCallback() {
    super.connectedCallback();
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-url', 'data-intent', 'data-limit'],
    });
    if (this.dataset.intent === 'recently_viewed') {
      window.addEventListener('recently-viewed:change', this.#historyChanged);
      window.addEventListener('storage', this.#storageChanged);
      this.#loadRecommendations();
    } else if (this.dataset.recommendationsPerformed !== 'true') {
      this.#intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    window.removeEventListener('recently-viewed:change', this.#historyChanged);
    window.removeEventListener('storage', this.#storageChanged);
  }

  #storageChanged = (event) => {
    if (event.key === 'viewedProducts' || event.key === null) this.#historyChanged();
  };

  #historyChanged = () => this.#loadRecommendations();

  async #loadRecommendations() {
    const { productId, sectionId, intent } = this.dataset;
    if (!productId || !sectionId) return;

    const isRecent = intent === 'recently_viewed';
    if (!isRecent && this.dataset.recommendationsPerformed === 'true') return;

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    const ids = isRecent
      ? RecentlyViewed.getProducts(12).filter((id) => id !== productId).slice(0, Number(this.dataset.limit) || 4)
      : [];
    if (isRecent && !ids.length) {
      this.hidden = true;
      this.#activeFetch = undefined;
      return;
    }

    const url = new URL(isRecent ? this.dataset.searchUrl : this.dataset.url, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    if (isRecent) {
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('resources[type]', 'product');
    } else {
      url.searchParams.set('product_id', productId);
      url.searchParams.set('intent', intent || 'related');
    }

    try {
      let html = this.#cachedRecommendations.get(url.href);
      if (!html) {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);
        html = await response.text();
        this.#cachedRecommendations.set(url.href, html);
      }
      if (controller.signal.aborted || !this.isConnected) return;

      const document = new DOMParser().parseFromString(html, 'text/html');
      const replacement = document.getElementById(this.id);
      if (!replacement) throw new Error('Missing recommendations section');
      if (isRecent) {
        // Search results are ranked by Shopify. Restore this visitor's visit order.
        const items = [...replacement.querySelectorAll('[data-discovery-product-id]')];
        const ordered = ids.map((id) => items.find((item) => item.dataset.discoveryProductId === id)).filter(Boolean);
        for (const item of items) {
          if (!ordered.includes(item)) (item.closest('slideshow-slide') || item).remove();
        }
        for (const item of ordered) {
          const slide = item.closest('slideshow-slide') || item;
          slide.parentElement?.append(slide);
        }
        replacement.hidden = !ordered.length;
        replacement.dataset.productId = productId;
      }
      replacement.dataset.recommendationsPerformed = 'true';
      replacement.classList.remove('hidden');
      replacement.removeAttribute('data-error');
      if (controller.signal.aborted || !this.isConnected) return;
      await morphSection(sectionId, document.documentElement.outerHTML, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      if (controller.signal.aborted || error.name === 'AbortError') return;
      this.hidden = true;
      this.dataset.error = 'Error loading product recommendations';
      console.error('Product recommendations:', error.message);
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = undefined;
    }
  }
}

/** Tabs and progressive disclosure around native, server-rendered product cards. */
class RecommendationTabs extends Component {
  #selected;
  #visible = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  #tabs() { return [...this.querySelectorAll('[role="tab"]')]; }

  #render() {
    const tabs = this.#tabs();
    if (!tabs.length) return;
    const selected = tabs.find((tab) => tab.id === this.#selected) || tabs[0];
    this.#selected = selected.id;
    const pageSize = Number(this.dataset.pageSize) || 4;
    let moreAvailable = false;
    for (const tab of tabs) {
      const active = tab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
      if (!panel) continue;
      panel.hidden = !active;
      const items = [...panel.querySelectorAll('.resource-list__item')];
      const visible = this.#visible.get(panel.id) || pageSize;
      items.forEach((item, index) => { item.hidden = index >= visible; });
      if (active) moreAvailable = items.length > visible;
    }
    const more = this.querySelector('[data-discovery-more]');
    if (more) {
      more.hidden = !moreAvailable;
      more.setAttribute('aria-controls', selected.getAttribute('aria-controls'));
    }
  }

  #onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) {
      this.#selected = tab.id;
      this.#render();
      return;
    }
    const more = event.target.closest('[data-discovery-more]');
    if (!more || !this.contains(more)) return;
    const panel = this.querySelector('[data-discovery-panel]:not([hidden])');
    if (!panel) return;
    const firstNew = panel.querySelector('.resource-list__item[hidden] a[href]');
    const pageSize = Number(this.dataset.pageSize) || 4;
    this.#visible.set(panel.id, (this.#visible.get(panel.id) || pageSize) + pageSize);
    this.#render();
    if (more.hidden) firstNew?.focus({ preventScroll: true });
  };

  #onKeydown = (event) => {
    const tabs = this.#tabs();
    const index = tabs.indexOf(event.target);
    if (index < 0) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + tabs.length) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + tabs.length) % tabs.length;
    else return;
    event.preventDefault();
    this.#selected = tabs[next].id;
    this.#render();
    tabs[next].focus();
  };
}

if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
if (!customElements.get('recommendation-tabs')) customElements.define('recommendation-tabs', RecommendationTabs);

